import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import Icon from '../common/Icons';
import { HEAD_DOCTOR_COPY } from './headDoctorCopy';

function MenuLink({ to, end, label, hint, icon, onNavigate, collapsed }) {
  return (
    <NavLink
      to={to}
      end={!!end}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) => `hd-menu-item${isActive ? ' is-active' : ''}`}
    >
      <span className="hd-menu-item__icon" aria-hidden>
        <Icon name={icon || 'list'} size={18} />
      </span>
      <span className="hd-menu-item__text">
        <span className="hd-menu-item__row">
          <span className="hd-menu-item__label">{label}</span>
        </span>
        {hint ? <span className="hd-menu-item__hint">{hint}</span> : null}
      </span>
    </NavLink>
  );
}

function FlyoutItems({ group, onNavigate, compact = false }) {
  return (
    <div className="hd-menu-flyout__items">
      {group.items.map(({ to, end, label, hint, icon }) => (
        <NavLink
          key={to}
          to={to}
          end={!!end}
          role="menuitem"
          onClick={onNavigate}
          className={({ isActive }) => `hd-menu-flyout__item${isActive ? ' is-active' : ''}`}
        >
          <span className="hd-menu-flyout__item-icon" aria-hidden>
            <Icon name={icon || 'list'} size={compact ? 15 : 16} />
          </span>
          <span className="hd-menu-flyout__item-text">
            <span className="hd-menu-flyout__item-label">{label}</span>
            {hint ? <span className="hd-menu-flyout__item-hint">{hint}</span> : null}
          </span>
        </NavLink>
      ))}
    </div>
  );
}

function HeadDoctorMenuGroup({ group, onNavigate, collapsed, mobile = false }) {
  const location = useLocation();
  const groupRef = useRef(null);
  const flyoutRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutReady, setFlyoutReady] = useState(false);
  const [flyoutCoords, setFlyoutCoords] = useState({
    top: 0,
    left: 0,
    bridgeTop: 0,
    bridgeLeft: 0,
    bridgeHeight: 0
  });

  const hasActiveChild = group.items.some(({ to }) => location.pathname.startsWith(to));

  const updateFlyoutPosition = useCallback(() => {
    const trigger = groupRef.current;
    const flyout = flyoutRef.current;
    if (!trigger || !flyout) return;

    const triggerRect = trigger.getBoundingClientRect();
    const flyoutHeight = flyout.offsetHeight;
    const viewportPadding = 12;
    const gap = 8;

    let top = triggerRect.top;
    const maxTop = window.innerHeight - flyoutHeight - viewportPadding;
    if (top > maxTop) {
      top = Math.max(viewportPadding, maxTop);
    }

    setFlyoutCoords({
      top,
      left: triggerRect.right + gap,
      bridgeTop: triggerRect.top,
      bridgeLeft: triggerRect.right,
      bridgeHeight: triggerRect.height
    });
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openFlyout = useCallback(() => {
    if (mobile) return;
    cancelClose();
    const triggerRect = groupRef.current?.getBoundingClientRect();
    if (triggerRect) {
      setFlyoutCoords((prev) => ({
        ...prev,
        bridgeTop: triggerRect.top,
        bridgeLeft: triggerRect.right,
        bridgeHeight: triggerRect.height
      }));
    }
    setFlyoutOpen(true);
  }, [mobile, cancelClose]);

  const closeFlyout = useCallback(() => {
    if (mobile) return;
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setFlyoutOpen(false);
      setFlyoutReady(false);
      closeTimerRef.current = null;
    }, 120);
  }, [mobile, cancelClose]);

  useLayoutEffect(() => {
    if (!flyoutOpen || mobile || !flyoutRef.current || !groupRef.current) return;
    updateFlyoutPosition();
    setFlyoutReady(true);
  }, [flyoutOpen, mobile, updateFlyoutPosition]);

  useEffect(() => {
    if (!flyoutOpen || mobile) return undefined;

    const onReflow = () => updateFlyoutPosition();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);

    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [flyoutOpen, mobile, updateFlyoutPosition]);

  useEffect(
    () => () => {
      cancelClose();
    },
    [cancelClose]
  );

  return (
    <div
      ref={groupRef}
      className={`hd-menu-group${hasActiveChild ? ' is-active' : ''}${mobileOpen ? ' is-open' : ''}${
        flyoutOpen ? ' is-flyout-open' : ''
      }${mobile ? ' hd-menu-group--mobile' : ''}`}
      onMouseEnter={openFlyout}
      onMouseLeave={closeFlyout}
    >
      <button
        type="button"
        className="hd-menu-group__trigger hd-menu-item"
        aria-expanded={mobile ? mobileOpen : flyoutOpen || hasActiveChild}
        aria-haspopup="menu"
        title={collapsed ? group.label : undefined}
        onClick={mobile ? () => setMobileOpen((open) => !open) : undefined}
      >
        <span className="hd-menu-item__icon" aria-hidden>
          <Icon name={group.icon || 'folder'} size={18} />
        </span>
        <span className="hd-menu-item__text">
          <span className="hd-menu-item__row">
            <span className="hd-menu-item__label">{group.label}</span>
            <Icon
              name="chevronRight"
              size={14}
              className={`hd-menu-group__chevron${mobileOpen ? ' is-open' : ''}`}
              aria-hidden
            />
          </span>
          {group.hint ? <span className="hd-menu-item__hint">{group.hint}</span> : null}
        </span>
      </button>

      {mobile ? (
        <div className="hd-menu-flyout hd-menu-flyout--mobile" role="menu" aria-label={group.label}>
          <FlyoutItems group={group} onNavigate={onNavigate} compact />
        </div>
      ) : null}

      {!mobile && flyoutOpen
        ? createPortal(
            <div className="head-doctor-portal hd-menu-flyout-portal">
              <div
                className="hd-menu-flyout-bridge"
                aria-hidden
                style={{
                  top: `${flyoutCoords.bridgeTop}px`,
                  left: `${flyoutCoords.bridgeLeft}px`,
                  width: '8px',
                  height: `${flyoutCoords.bridgeHeight}px`
                }}
                onMouseEnter={openFlyout}
                onMouseLeave={closeFlyout}
              />
              <div
                ref={flyoutRef}
                className={`hd-menu-flyout hd-menu-flyout--pop${flyoutReady ? ' is-ready' : ''}`}
                role="menu"
                aria-label={group.label}
                style={{ top: `${flyoutCoords.top}px`, left: `${flyoutCoords.left}px` }}
                onMouseEnter={openFlyout}
                onMouseLeave={closeFlyout}
              >
                <div className="hd-menu-flyout__head">{group.label}</div>
                <FlyoutItems group={group} onNavigate={onNavigate} />
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default function HeadDoctorNavMenu({ menuModel, onNavigate, collapsed = false, mobile = false }) {
  const layout = HEAD_DOCTOR_COPY.LAYOUT;
  const { quickItems = [], groups = [] } = menuModel || {};

  return (
    <>
      <div className="hd-menu-section">
        <div className="hd-menu-section__label">{layout.SIDE_QUICK_ACCESS}</div>
        <div className="hd-menu-section__items">
          {quickItems.map(({ to, end, label, hint, icon }) => (
            <MenuLink
              key={to}
              to={to}
              end={end}
              label={label}
              hint={hint}
              icon={icon}
              onNavigate={onNavigate}
              collapsed={collapsed}
            />
          ))}
        </div>
      </div>

      {groups.length > 0 ? (
        <div className="hd-menu-section">
          <div className="hd-menu-section__label">{layout.SIDE_MENU_CATALOGS}</div>
          <div className="hd-menu-section__items">
            {groups.map((group) => (
              <HeadDoctorMenuGroup
                key={group.key}
                group={group}
                onNavigate={onNavigate}
                collapsed={collapsed}
                mobile={mobile}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
